import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  TrendingUp, 
  Target, 
  Users, 
  BarChart3, 
  CheckCircle, 
  ArrowRight,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Globe,
  Shield,
  Zap,
  Clock,
  Award,
  PieChart,
  LineChart,
  Activity,
  UserCheck,
  Briefcase,
  Settings
} from 'lucide-react';

const Sales: React.FC = () => {
  const salesSolutions = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Lead Management",
      description: "Capture, qualify, and nurture leads through intelligent automation and scoring systems.",
      features: ["Lead scoring", "Automated nurturing", "Source tracking", "Conversion analytics"]
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Sales Pipeline",
      description: "Visualize and manage your sales pipeline with real-time insights and forecasting.",
      features: ["Visual pipeline", "Deal tracking", "Revenue forecasting", "Stage automation"]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer Relationship Management",
      description: "Build stronger relationships with comprehensive customer profiles and interaction history.",
      features: ["360° customer view", "Interaction history", "Contact management", "Relationship mapping"]
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Sales Analytics",
      description: "Make data-driven decisions with advanced reporting and performance analytics.",
      features: ["Performance dashboards", "Sales reports", "Trend analysis", "KPI tracking"]
    }
  ];

  const benefits = [
    "Increase sales productivity by 40%",
    "Improve lead conversion rates by 35%",
    "Reduce sales cycle time by 25%",
    "Enhance customer satisfaction scores",
    "Automate 70% of routine sales tasks",
    "Gain real-time sales visibility"
  ];

  const salesFeatures = [
    {
      title: "Opportunity Management",
      description: "Track and manage sales opportunities from initial contact to closed deal.",
      icon: <Briefcase className="w-8 h-8" />
    },
    {
      title: "Quote & Proposal Generation",
      description: "Create professional quotes and proposals with automated pricing and approval workflows.",
      icon: <DollarSign className="w-8 h-8" />
    },
    {
      title: "Sales Forecasting",
      description: "Predict future sales performance with AI-powered forecasting and trend analysis.",
      icon: <TrendingUp className="w-8 h-8" />
    },
    {
      title: "Territory Management",
      description: "Optimize sales territories and assign leads based on geographic and demographic criteria.",
      icon: <Globe className="w-8 h-8" />
    },
    {
      title: "Mobile Sales App",
      description: "Access customer data, update records, and manage activities from anywhere with mobile apps.",
      icon: <Phone className="w-8 h-8" />
    },
    {
      title: "Sales Automation",
      description: "Automate repetitive tasks like follow-ups, data entry, and workflow approvals.",
      icon: <Zap className="w-8 h-8" />
    }
  ];

  const salesProcess = [
    {
      step: "01",
      title: "Lead Capture",
      description: "Automatically capture leads from multiple channels including web forms, social media, and marketing campaigns.",
      color: "bg-blue-500"
    },
    {
      step: "02", 
      title: "Lead Qualification",
      description: "Score and qualify leads using AI-powered algorithms and predefined criteria to focus on high-value prospects.",
      color: "bg-green-500"
    },
    {
      step: "03",
      title: "Opportunity Development",
      description: "Convert qualified leads into opportunities and track them through your customized sales pipeline stages.",
      color: "bg-purple-500"
    },
    {
      step: "04",
      title: "Proposal & Negotiation",
      description: "Generate professional proposals, track negotiations, and manage approvals with automated workflows.",
      color: "bg-orange-500"
    },
    {
      step: "05",
      title: "Deal Closure",
      description: "Close deals efficiently with e-signature integration, contract management, and automated follow-up processes.",
      color: "bg-red-500"
    },
    {
      step: "06",
      title: "Customer Success",
      description: "Transition closed deals to customer success teams and continue relationship management for upselling opportunities.",
      color: "bg-indigo-500"
    }
  ];

  const integrations = [
    "Microsoft Office 365",
    "Outlook & Teams Integration",
    "LinkedIn Sales Navigator",
    "Marketing Automation Platforms",
    "ERP Systems",
    "E-commerce Platforms",
    "Social Media Channels",
    "Third-party Applications"
  ];

  return (
    <>
      <SEOHead
        title="Sales Solutions | CRMONCE - Microsoft Dynamics 365 Sales CRM"
        description="Boost your sales performance with Microsoft Dynamics 365 Sales. Expert CRM implementation, sales automation, and pipeline management. Increase sales productivity by 40% with CRMONCE."
        keywords="Dynamics 365 Sales, CRM software, sales automation, lead management, sales pipeline, customer relationship management, sales analytics, Microsoft CRM, sales forecasting, opportunity management, quote generation"
        canonical="https://crmonce.com/sales"
        ogImage="/images/sales-crm-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Microsoft Dynamics 365 Sales Solutions",
          "description": "Professional Microsoft Dynamics 365 Sales CRM implementation and sales automation services. Increase sales productivity by 40% with intelligent CRM solutions.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "serviceType": "CRM Sales Solutions",
          "category": "Customer Relationship Management",
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "offers": {
            "@type": "Offer",
            "description": "Professional Dynamics 365 Sales implementation and consulting services",
            "category": "CRM Consulting Services"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Sales CRM Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Lead Management",
                  "description": "Capture, qualify, and nurture leads through intelligent automation"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Sales Pipeline Management",
                  "description": "Visualize and manage sales pipeline with real-time insights"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Customer Relationship Management",
                  "description": "Build stronger relationships with comprehensive customer profiles"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <TrendingUp className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Dynamics 365 Sales Solutions</h1>
              </div>
              <p className="text-xl text-blue-100 mb-8">
                Transform your sales process with intelligent CRM solutions. Accelerate deals, 
                build stronger relationships, and drive revenue growth with Microsoft Dynamics 365 Sales.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
              >
                Start Your Sales Transformation
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Sales Solutions */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Sales Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From lead generation to deal closure, our Dynamics 365 Sales solutions 
                cover every aspect of your sales process.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {salesSolutions.map((solution, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">{solution.icon}</div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 mb-6">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Sales Process */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Streamlined Sales Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Follow a proven sales methodology that guides your team from initial 
                lead capture to successful deal closure and beyond.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {salesProcess.map((process, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow relative"
                >
                  <div className={`absolute -top-4 -left-4 w-12 h-12 ${process.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 mt-4">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Sales Features */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Powerful Sales Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Leverage advanced features designed to enhance every aspect of your sales operations.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {salesFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="text-blue-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Our Sales Solutions?
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Transform your sales organization with proven solutions that deliver measurable results.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Seamless Integrations
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Connect your sales system with the tools your team already uses 
                for a unified business ecosystem.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {integrations.map((integration, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-6"
                >
                  <Settings className="w-6 h-6 text-blue-600 mr-4 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{integration}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-blue-900">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Accelerate Your Sales?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let our Dynamics 365 Sales experts help you implement a solution that 
                drives revenue growth and enhances customer relationships.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Get Free Sales Assessment
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  View Success Stories
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Sales;
